/**
 * Fantasy Console 0 - Sign Up Modal
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { useAuth } from '../../context/AuthContext'
import { getAuthErrorMessage } from '../../utils/authErrors'
import styles from './AuthModal.module.css'

interface SignUpModalProps {
  isOpen: boolean
  onClose: () => void
  onSwitchToSignIn: () => void
}

export function SignUpModal({
  isOpen,
  onClose,
  onSwitchToSignIn,
}: SignUpModalProps) {
  const { signUp } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [signUpComplete, setSignUpComplete] = useState(false)
  const emailInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen) {
      setEmail('')
      setPassword('')
      setError(null)
      setSignUpComplete(false)
      setTimeout(() => emailInputRef.current?.focus(), 100)
    }
  }, [isOpen])

  const validateForm = (): string | null => {
    if (!email.trim()) {
      return 'Please enter an email address'
    }

    if (password.length < 6) {
      return 'Password must be at least 6 characters'
    }

    return null
  }

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setError(null)

      const validationError = validateForm()
      if (validationError) {
        setError(validationError)
        return
      }

      setIsLoading(true)

      try {
        await signUp(email.trim(), password)
        setSignUpComplete(true)
      } catch (err) {
        setError(getAuthErrorMessage(err))
      } finally {
        setIsLoading(false)
      }
    },
    [email, password, signUp],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose],
  )

  if (!isOpen) return null

  // Show success message after sign up
  if (signUpComplete) {
    return (
      <div
        className='modal-overlay'
        onClick={onClose}
        onKeyDown={handleKeyDown}
      >
        <div className='modal-content' onClick={(e) => e.stopPropagation()}>
          <div className='modal-header'>
            <h2 className='modal-title'>Check Your Email</h2>
          </div>

          <div className='modal-body'>
            <div className={styles.successMessage}>
              <EmailIcon />
              <p>
                We've sent a verification link to <strong>{email}</strong>
              </p>
              <p className={styles.successHint}>
                Please check your inbox and click the link to verify your
                account. Once verified, you can sign in.
              </p>
            </div>
          </div>

          <div className='modal-footer'>
            <button type='button' className='btn btn-ghost' onClick={onClose}>
              Close
            </button>
            <button
              type='button'
              className='btn btn-primary'
              onClick={() => {
                setSignUpComplete(false)
                onSwitchToSignIn()
              }}
            >
              Go to Sign In
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='modal-overlay' onClick={onClose} onKeyDown={handleKeyDown}>
      <div className='modal-content' onClick={(e) => e.stopPropagation()}>
        <div className='modal-header'>
          <h2 className='modal-title'>Create Account</h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className='modal-body'>
            {error && <div className={styles.error}>{error}</div>}

            <div className='form-group'>
              <label className='form-label' htmlFor='signup-email'>
                Email
              </label>
              <input
                ref={emailInputRef}
                id='signup-email'
                type='email'
                className='form-input'
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setError(null)
                }}
                placeholder='you@example.com'
                autoComplete='email'
                required
              />
            </div>

            <div className='form-group'>
              <label className='form-label' htmlFor='signup-password'>
                Password
              </label>
              <input
                id='signup-password'
                type='password'
                className='form-input'
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(null)
                }}
                placeholder='At least 6 characters'
                autoComplete='new-password'
                minLength={6}
                required
              />
            </div>

            <div className={styles.switchPrompt}>
              Already have an account?{' '}
              <button
                type='button'
                className={styles.switchLink}
                onClick={onSwitchToSignIn}
              >
                Sign in
              </button>
            </div>
          </div>

          <div className='modal-footer'>
            <button type='button' className='btn btn-ghost' onClick={onClose}>
              Cancel
            </button>
            <button
              type='submit'
              className='btn btn-primary'
              disabled={isLoading}
            >
              {isLoading ? 'Creating account...' : 'Create Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function EmailIcon() {
  return (
    <svg
      width='48'
      height='48'
      viewBox='0 0 48 48'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
    >
      <rect x='4' y='8' width='40' height='32' rx='2' />
      <path d='M4 12l20 14 20-14' />
    </svg>
  )
}
